import { useState } from "react";
import { toast } from "sonner";
import { Wallet, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const FIELDS = [
  ["payment_method", "Payment method / type", "text", "e.g. Wire, ACH, Letter of Credit, Escrow"],
  ["deposit_amount", "Deposit amount", "number", "e.g. 25000"],
  ["deposit_pct", "Deposit %", "number", "e.g. 20"],
  ["deposit_due_date", "Deposit due date", "text", "e.g. 2026-10-05 or 'on signing'"],
  ["payment_schedule", "Milestone / payment schedule", "area", "e.g. 20% deposit, 50% on shipment, 30% on acceptance"],
  ["balance_due_conditions", "Balance due conditions", "area", "e.g. Balance due within 5 days of delivery acceptance"],
  ["escrow_provider", "Escrow / payment provider", "text", "e.g. (simulated) Provider name"],
  ["funding_conditions", "Funding conditions", "area", "e.g. Buyer funds escrow before dispatch"],
  ["release_conditions", "Release conditions", "area", "e.g. Release on signed delivery + passed inspection"],
  ["dispute_hold_conditions", "Dispute / hold conditions", "area", "e.g. Hold funds on any raised dispute"],
  ["acceptance_window", "Acceptance window", "text", "e.g. 7 days from delivery"],
];

// Structured payment & settlement terms. These feed the Deal Terms Guard: a material change
// after legal sign-off flags RE-REVIEW REQUIRED. Advisory / SIMULATED — no live funds move.
export default function PaymentTermsPanel({ deal, onSaved }) {
  const [form, setForm] = useState(() => {
    const f = {}; FIELDS.forEach(([k]) => { f[k] = deal[k] ?? ""; }); return f;
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Advisory live hint — never overwrites the entered deposit amount.
  const total = Number(deal.total_value) || 0;
  const pct = form.deposit_pct === "" ? null : Number(form.deposit_pct);
  const entered = form.deposit_amount === "" ? null : Number(form.deposit_amount);
  const cur = deal.currency || "";
  const expectedDeposit = total && pct != null && !Number.isNaN(pct) ? Math.round(total * pct) / 100 : null;
  const tol = expectedDeposit != null ? Math.max(1, 0.005 * expectedDeposit) : 0;
  const depositMismatch = expectedDeposit != null && entered != null && !Number.isNaN(entered)
    && Math.abs(entered - expectedDeposit) > tol;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      FIELDS.forEach(([k, , type]) => {
        let v = form[k];
        if (type === "number") v = v === "" ? null : Number(v);
        else v = v === "" ? null : v;
        payload[k] = v;
      });
      const { data } = await api.patch(`/deals/${deal.deal_id}`, payload);
      const reReview = data.contracts_re_review_required || [];
      if (reReview.length) {
        toast.warning(`Saved. ${reReview.length} contract(s) flagged RE-REVIEW REQUIRED — the assigned reviewer was notified.`);
      } else {
        toast.success("Payment & settlement terms saved");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save terms");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="payment-terms-panel">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Wallet size={15} /> Payment &amp; settlement terms</h4>
        <button data-testid="payment-terms-save" onClick={save} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null} Save terms
        </button>
      </div>
      <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
        These are advisory / SIMULATED — no live funds move. Changing a material term after a legal sign-off will flag the contract RE-REVIEW REQUIRED and notify the assigned reviewer.
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {FIELDS.map(([k, label, type, ph]) => (
          <div key={k} className={type === "area" ? "sm:col-span-2" : ""}>
            <Label htmlFor={`pt-${k}`} className="text-xs">{label}</Label>
            {type === "area" ? (
              <Textarea id={`pt-${k}`} data-testid={`pt-${k}`} rows={2} value={form[k]} placeholder={ph} onChange={(e) => set(k, e.target.value)} />
            ) : (
              <Input id={`pt-${k}`} data-testid={`pt-${k}`} type={type} value={form[k]} placeholder={ph} onChange={(e) => set(k, e.target.value)} />
            )}
          </div>
        ))}
      </div>
      {expectedDeposit != null && (
        <div data-testid="deposit-live-hint" className={`mt-3 rounded-md border p-2 text-[11px] ${depositMismatch ? "border-orange-300 bg-orange-50 text-orange-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
          Expected deposit at {pct}% of {cur} {total.toLocaleString()} = <strong>{cur} {expectedDeposit.toLocaleString()}</strong>
          {entered != null && !Number.isNaN(entered) && (
            depositMismatch
              ? <span> · entered {cur} {entered.toLocaleString()} disagrees — reconcile before signature (advisory; your entry is not overwritten).</span>
              : <span className="text-emerald-700"> · matches your entered amount.</span>
          )}
        </div>
      )}
    </div>
  );
}
